import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { STUDENT_STATUSES, MODES, PAYMENT_STATUSES, GENDERS, fmtMoney } from "@/lib/studentConstants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

const EMPTY = {
  full_name: "", mobile: "", alt_mobile: "", email: "", gender: "", dob: "", address: "",
  city: "", state: "", pincode: "", course_enrolled: "", course_fee: "", discount: "",
  admission_date: "", admission_counselor: "", trainer: "", batch: "", batch_timing: "",
  batch_start_date: "", mode: "Online", payment_status: "Pending", total_paid: "",
  next_due_amount: "", next_due_date: "", expected_completion_date: "",
  job_guarantee_eligible: false, status: "Active", remarks: "",
};

function Section({ title, children }) {
  return (
    <div>
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">{title}</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}

// Module-level so its identity is stable across parent re-renders (prevents focus loss on typing).
function Field({ label, value, onChange, type = "text", placeholder, error, testid }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input type={type} value={value} onChange={onChange} placeholder={placeholder} data-testid={testid} />
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export default function StudentFormDialog({ open, onOpenChange, student, onSave }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [opts, setOpts] = useState({ courses: [], trainers: [], batches: [], counselors: [] });
  const isEdit = Boolean(student?.id);

  useEffect(() => {
    if (open) {
      setForm(student?.id
        ? { ...EMPTY, ...student, course_fee: student.course_fee ?? "", discount: student.discount ?? "", total_paid: student.total_paid ?? "", next_due_amount: student.next_due_amount ?? "" }
        : EMPTY);
      setErrors({});
    }
  }, [open, student]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [c, t, b, u] = await Promise.all([
          api.get("/courses", { params: { page_size: 200 } }),
          api.get("/trainers", { params: { page_size: 200 } }),
          api.get("/batches", { params: { page_size: 200 } }),
          api.get("/leads/executives").catch(() => ({ data: [] })),
        ]);
        setOpts({
          courses: (c.data.items || []).map((x) => x.name).filter(Boolean),
          trainers: (t.data.items || []).map((x) => x.name).filter(Boolean),
          batches: (b.data.items || []).map((x) => ({ name: x.name, timing: x.timing || "" })).filter((x) => x.name),
          counselors: (u.data || []).map((x) => x.name).filter(Boolean),
        });
      } catch { /* dropdowns just stay empty */ }
    })();
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (v) => parseFloat(v) || 0;
  const finalFee = Math.max(num(form.course_fee) - num(form.discount), 0);
  const pending = Math.max(finalFee - num(form.total_paid), 0);

  // Render helper (called as a function, NOT used as <F/>) so inputs keep a stable position.
  const field = (label, k, type = "text", placeholder) => (
    <Field
      label={label}
      value={form[k]}
      onChange={(e) => set(k, e.target.value)}
      type={type}
      placeholder={placeholder}
      error={errors[k]}
      testid={`student-${k}-input`}
    />
  );

  const selectField = (label, k, options, placeholder, testid) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={form[k] || ""} onValueChange={(v) => set(k, v)}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>
          {options.length === 0
            ? <SelectItem value="__none" disabled>No options available</SelectItem>
            : options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const onBatchChange = (name) => {
    const b = opts.batches.find((x) => x.name === name);
    setForm((f) => ({ ...f, batch: name, batch_timing: b ? b.timing : f.batch_timing }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = {};
    if (!form.full_name || form.full_name.trim().length < 2) err.full_name = "Enter a valid name";
    if (!form.mobile || form.mobile.trim().length < 6) err.mobile = "Enter a valid mobile";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Enter a valid email";
    setErrors(err);
    if (Object.keys(err).length) return;
    setSaving(true);
    try {
      const payload = { ...form, course_fee: num(form.course_fee), discount: num(form.discount), total_paid: num(form.total_paid), next_due_amount: num(form.next_due_amount) };
      await onSave(payload, isEdit ? student.id : null);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="student-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${student.student_code}` : "New Student"}</DialogTitle>
          <DialogDescription>Maintain the complete student profile.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-6">
          <Section title="Personal Details">
            {field("Full Name *", "full_name")}
            {field("Mobile Number *", "mobile")}
            {field("Alternative Mobile", "alt_mobile")}
            {field("Email", "email", "email")}
            <div className="space-y-1.5">
              <Label>Gender</Label>
              <Select value={form.gender || "unset"} onValueChange={(v) => set("gender", v === "unset" ? "" : v)}>
                <SelectTrigger data-testid="student-gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="unset">Not specified</SelectItem>{GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {field("Date of Birth", "dob", "date")}
            {field("City", "city")}
            {field("State", "state")}
            {field("Pincode", "pincode")}
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Address</Label>
              <Textarea rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="student-address-input" />
            </div>
          </Section>

          <Section title="Course & Fees">
            {selectField("Course Enrolled", "course_enrolled", opts.courses, "Select a course", "student-course-select")}
            {field("Admission Date", "admission_date", "date")}
            {field("Course Fee (₹)", "course_fee", "number")}
            {field("Discount (₹)", "discount", "number")}
            <div className="space-y-1.5">
              <Label>Final Fee</Label>
              <div className="flex h-10 items-center rounded-md border border-border bg-slate-50 px-3 text-sm font-medium text-secondary" data-testid="student-final-fee">{fmtMoney(finalFee)}</div>
            </div>
            {field("Total Paid (₹)", "total_paid", "number")}
            <div className="space-y-1.5">
              <Label>Pending Amount</Label>
              <div className="flex h-10 items-center rounded-md border border-border bg-slate-50 px-3 text-sm font-medium text-rose-600" data-testid="student-pending">{fmtMoney(pending)}</div>
            </div>
            <div className="space-y-1.5">
              <Label>Payment Status</Label>
              <Select value={form.payment_status} onValueChange={(v) => set("payment_status", v)}>
                <SelectTrigger data-testid="student-payment-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {field("Next Due Amount (₹)", "next_due_amount", "number")}
            {field("Next Due Date", "next_due_date", "date")}
          </Section>

          <Section title="Batch & Training">
            {selectField("Admission Counselor", "admission_counselor", opts.counselors, "Select counselor", "student-counselor-select")}
            {selectField("Trainer", "trainer", opts.trainers, "Select trainer", "student-trainer-select")}
            <div className="space-y-1.5">
              <Label>Batch</Label>
              <Select value={form.batch || ""} onValueChange={onBatchChange}>
                <SelectTrigger data-testid="student-batch-select"><SelectValue placeholder="Select batch" /></SelectTrigger>
                <SelectContent>
                  {opts.batches.length === 0
                    ? <SelectItem value="__none" disabled>No batches available</SelectItem>
                    : opts.batches.map((b) => <SelectItem key={b.name} value={b.name}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Batch Timing</Label>
              <div className="flex h-10 items-center rounded-md border border-border bg-slate-50 px-3 text-sm text-secondary" data-testid="student-batch-timing">
                {form.batch_timing || "—"}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Mode</Label>
              <Select value={form.mode} onValueChange={(v) => set("mode", v)}>
                <SelectTrigger data-testid="student-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Student Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="student-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STUDENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2 sm:col-span-2">
              <div>
                <Label>Job Guarantee Eligible</Label>
                <p className="text-xs text-muted-foreground">Eligible for the job guarantee program</p>
              </div>
              <Switch checked={form.job_guarantee_eligible} onCheckedChange={(v) => set("job_guarantee_eligible", v)} data-testid="student-jobguarantee-switch" />
            </div>
          </Section>

          <div className="space-y-1.5">
            <Label>Remarks</Label>
            <Textarea rows={2} value={form.remarks} onChange={(e) => set("remarks", e.target.value)} data-testid="student-remarks-input" />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-student-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save Changes" : "Create Student"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
